import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import type { OrgTrade, TradePack, TradeTerminology, TradeNavVisibility } from "@/types/trade";

const ACTIVE_TRADE_KEY = "rg_active_trade_code";

const DEFAULT_TERMINOLOGY: TradeTerminology = {
  job: "Job", jobs: "Jobs", crew: "Crew", crews: "Crews",
  measurement: "Measurement", unit: "Unit", units: "Units",
};

const DEFAULT_NAV: TradeNavVisibility = {
  insurance: true, supplements: true, drone: true,
  field_scout: true, storm_watch: true,
};

interface UseOrgTradesResult {
  loading: boolean;
  orgTrades: OrgTrade[];
  activeTradeCode: string | null;
  activeTradePack: TradePack | null;
  packsByCode: Record<string, TradePack>;
  setActiveTradeCode: (code: string) => void;
  refetch: () => Promise<void>;
  // Convenience term lookup with sane fallback
  t: (key: keyof TradeTerminology, fallback?: string) => string;
  // Nav visibility helper (true if not configured)
  isFeatureVisible: (feature: keyof TradeNavVisibility) => boolean;
}

export function useOrgTrades(): UseOrgTradesResult {
  const { orgId } = useAuth();
  const [loading, setLoading] = useState(true);
  const [orgTrades, setOrgTrades] = useState<OrgTrade[]>([]);
  const [packsByCode, setPacksByCode] = useState<Record<string, TradePack>>({});
  const [activeTradeCode, setActiveTradeCodeState] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    if (!orgId) {
      setOrgTrades([]);
      setPacksByCode({});
      setActiveTradeCodeState(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const { data: trades, error: tErr } = await supabase
        .rpc("get_org_active_trades", { _org_id: orgId });

      if (tErr) throw tErr;

      const list: OrgTrade[] = (trades ?? []).filter((t: any) => t.is_active);
      setOrgTrades(list);

      if (list.length === 0) {
        setPacksByCode({});
        setActiveTradeCodeState(null);
        setLoading(false);
        return;
      }

      const codes = list.map((t) => t.trade_code);
      const { data: packs, error: pErr } = await supabase
        .from("trade_packs")
        .select("*")
        .in("trade_code", codes);

      if (pErr) throw pErr;

      const map: Record<string, TradePack> = {};
      for (const p of packs ?? []) {
        map[p.trade_code] = p as unknown as TradePack;
      }
      setPacksByCode(map);

      // Restore last active trade or default to first enabled
      const stored = typeof window !== "undefined"
        ? window.localStorage.getItem(`${ACTIVE_TRADE_KEY}:${orgId}`)
        : null;
      const initial = stored && codes.includes(stored) ? stored : codes[0];
      setActiveTradeCodeState(initial);
    } catch (err) {
      console.error("[useOrgTrades] fetch failed:", err);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const setActiveTradeCode = useCallback((code: string) => {
    setActiveTradeCodeState(code);
    if (orgId && typeof window !== "undefined") {
      window.localStorage.setItem(`${ACTIVE_TRADE_KEY}:${orgId}`, code);
    }
  }, [orgId]);

  const activeTradePack = activeTradeCode ? packsByCode[activeTradeCode] ?? null : null;

  const t = useCallback(
    (key: keyof TradeTerminology, fallback?: string): string => {
      const term = activeTradePack?.terminology?.[key];
      return term ?? fallback ?? DEFAULT_TERMINOLOGY[key] ?? String(key);
    },
    [activeTradePack],
  );

  const isFeatureVisible = useCallback(
    (feature: keyof TradeNavVisibility): boolean => {
      const nav = activeTradePack?.nav_visibility;
      if (!nav) return DEFAULT_NAV[feature] ?? true;
      const v = nav[feature];
      return v === undefined ? true : v;
    },
    [activeTradePack],
  );

  return {
    loading,
    orgTrades,
    activeTradeCode,
    activeTradePack,
    packsByCode,
    setActiveTradeCode,
    refetch: fetchAll,
    t,
    isFeatureVisible,
  };
}
